import type { BotController } from '../controllers/BotController.ts';
import type { StimulationController } from '../controllers/StimulationController.ts';
import type { Controller } from '../controllers/Controller.ts';
import type { GameState } from '../game/GameState.ts';
import type { ExperimentSession } from '../experiments/ExperimentSession.ts';
import { STAGE_LABELS, type LatencyStage } from '../experiments/LatencyTracker.ts';
import { el, FieldGroup, fmtMs, fmtPct, panel } from './dom.ts';

export interface DashboardSources {
  session: ExperimentSession;
  controller: () => Controller;
  bot: () => BotController | null;
  stimController: () => StimulationController | null;
  fps: () => number;
  modeLabel: () => string;
}

/**
 * The researcher's instrument panel.
 *
 * Four groups, matching the four questions asked while a trial is running:
 * is the game behaving, what is the controller thinking, is the human interface
 * responding, and where is the time going.
 *
 * Updated at 10 Hz rather than every frame. The numbers here are read by a
 * person, and a value that changes 120 times a second is unreadable.
 */
export class ExperimentDashboard {
  readonly root: HTMLElement;

  private game = new FieldGroup();
  private controllerFields = new FieldGroup();
  private human = new FieldGroup();
  private latencyTable: HTMLTableSectionElement;
  private reasonEl: HTMLElement;
  private lastUpdate = 0;

  constructor(private readonly sources: DashboardSources) {
    this.root = el('div', 'dashboard');

    // ---------------------------------------------------------------- game --
    const gamePanel = panel('Game');
    for (const [k, label] of [
      ['fps', 'FPS'],
      ['tick', 'Simulation tick'],
      ['simTime', 'Sim time'],
      ['score', 'Score'],
      ['best', 'Best this session'],
      ['alive', 'State'],
      ['flapRate', 'Flaps / s'],
      ['birdY', 'Bird Y'],
      ['birdVy', 'Bird velocity Y'],
      ['gap', 'Next gap'],
      ['distance', 'Distance to pipe'],
      ['ttp', 'Time to pipe'],
      ['seed', 'Seed'],
    ] as [string, string][]) {
      this.game.add(k, label);
    }
    gamePanel.body.appendChild(this.game.root);

    // ---------------------------------------------------------- controller --
    const controllerPanel = panel('Controller');
    for (const [k, label, hint] of [
      ['mode', 'Mode', ''],
      ['controller', 'Active controller', ''],
      ['flapRequested', 'Flap requested', 'The bot formed an intent on this tick'],
      ['flapExecuted', 'Flaps executed', 'Impulses actually applied to the bird'],
      ['target', 'Target Y', 'Aim point: gap centre plus the aim offset'],
      ['predicted', 'Predicted Y', 'Where the bird will be when a request issued now lands'],
      ['error', 'Error', 'predicted − target; positive means too low'],
      ['horizon', 'Compensation horizon', 'How far ahead the bot extrapolates'],
      ['inFlight', 'Impulses in flight', 'Requested, not yet observed as flaps'],
    ] as [string, string, string][]) {
      this.controllerFields.add(k, label, hint || undefined);
    }
    controllerPanel.body.appendChild(this.controllerFields.root);
    this.reasonEl = el('div', 'reason', '—');
    controllerPanel.body.appendChild(this.reasonEl);

    // ------------------------------------------------------ human interface --
    const humanPanel = panel('Human interface');
    for (const [k, label, hint] of [
      ['stimulator', 'Stimulator', ''],
      ['connected', 'Connected', ''],
      ['armed', 'Armed', ''],
      ['requests', 'Stimulation requests', ''],
      ['rejected', 'Rejected by safety layer', 'Rate limit, refractory, or disarmed'],
      ['movements', 'Successful movements', 'Inputs detected and attributed to a request'],
      ['missed', 'Missed movements', 'Requests that timed out with no response'],
      ['unattributed', 'Unattributed inputs', 'Inputs with no plausible parent request'],
      ['successRate', 'Command success rate', 'movements / requests'],
      ['device', 'Input device', ''],
    ] as [string, string, string][]) {
      this.human.add(k, label, hint || undefined);
    }
    humanPanel.body.appendChild(this.human.root);

    // ------------------------------------------------------------- latency --
    const latencyPanel = panel('Latency');
    const table = el('table', 'latency-table');
    const thead = el('thead');
    const hrow = el('tr');
    for (const h of ['stage', 'n', 'current', 'median', 'mean', 'p95', 'jitter']) {
      hrow.appendChild(el('th', h === 'stage' ? 'stage-col' : '', h));
    }
    thead.appendChild(hrow);
    this.latencyTable = el('tbody');
    table.append(thead, this.latencyTable);
    latencyPanel.body.appendChild(table);

    this.root.append(gamePanel.root, controllerPanel.root, humanPanel.root, latencyPanel.root);
  }

  private best = 0;

  update(state: GameState, nowMs: number, force = false): void {
    if (!force && nowMs - this.lastUpdate < 100) return;
    this.lastUpdate = nowMs;

    const { session } = this.sources;
    this.best = Math.max(this.best, state.score);

    // ---- game ----
    this.game.set('fps', this.sources.fps().toFixed(0));
    this.game.set('tick', String(state.tick));
    this.game.set('simTime', `${(state.simTimeMs / 1000).toFixed(2)} s`);
    this.game.set('score', String(state.score));
    this.game.set('best', String(this.best));
    this.game.set(
      'alive',
      session.paused ? 'paused' : state.alive ? 'running' : 'game over',
      session.paused ? 'warn' : state.alive ? 'ok' : 'bad',
    );
    // The stimulation budget, live. In a mediated run this is how often the
    // participant is being stimulated, which is the number that decides whether
    // a protocol is acceptable.
    //
    // Both figures are shown because the rate is bursty: the bot coasts, then
    // flaps several times to absorb a step between gaps. A short window reads
    // 2.8/s inside a burst on a course whose real cost is 1.1/s, so the session
    // average is the budget and the window is the peak load.
    const sessionRate =
      state.simTimeMs > 0 ? session.engine.totalFlaps / (state.simTimeMs / 1000) : 0;
    const windowRate = session.engine.recentFlapRate(15);
    this.game.set(
      'flapRate',
      state.simTimeMs > 500
        ? `${sessionRate.toFixed(2)}  (15 s: ${windowRate.toFixed(2)})`
        : '—',
      sessionRate > 2.5 ? 'bad' : sessionRate > 1.5 ? 'warn' : 'ok',
    );
    this.game.set('birdY', state.birdY.toFixed(1));
    this.game.set('birdVy', `${state.birdVelocityY.toFixed(0)} px/s`);
    this.game.set(
      'gap',
      `${state.nextPipeGapTop.toFixed(0)} – ${state.nextPipeGapBottom.toFixed(0)}`,
    );
    const distance = state.nextPipeX - state.birdX;
    this.game.set('distance', `${distance.toFixed(0)} px`);
    this.game.set('ttp', `${((distance / state.pipeSpeed) * 1000).toFixed(0)} ms`);
    this.game.set('seed', String(session.engine.config.seed));

    // ---- controller ----
    const controller = this.sources.controller();
    const bot = this.sources.bot();
    const decision = bot?.lastDecision ?? null;

    this.controllerFields.set('mode', this.sources.modeLabel());
    this.controllerFields.set('controller', controller.label);
    if (decision) {
      this.controllerFields.set(
        'flapRequested',
        decision.flap ? 'YES' : decision.suppressed ? 'suppressed' : 'no',
        decision.flap ? 'ok' : decision.suppressed ? 'warn' : 'muted',
      );
      this.controllerFields.set('target', decision.targetY.toFixed(1));
      this.controllerFields.set('predicted', decision.predictedY.toFixed(1));
      this.controllerFields.set(
        'error',
        `${decision.errorY >= 0 ? '+' : ''}${decision.errorY.toFixed(1)} px`,
        Math.abs(decision.errorY) > 40 ? 'warn' : undefined,
      );
      this.controllerFields.set(
        'horizon',
        decision.horizonMs > 0 ? fmtMs(decision.horizonMs) : 'off (direct)',
        decision.horizonMs > 0 ? 'ok' : 'muted',
      );
      this.controllerFields.set('inFlight', String(decision.inFlight));
      this.reasonEl.textContent = decision.reason;
    } else {
      for (const k of ['flapRequested', 'target', 'predicted', 'error', 'horizon', 'inFlight']) {
        this.controllerFields.set(k, '—', 'muted');
      }
      this.reasonEl.textContent = 'waiting for the game to start';
    }
    this.controllerFields.set('flapExecuted', String(session.engine.totalFlaps));

    // ---- human interface ----
    const status = session.stimulation.getStatus();
    const counters = session.stimulation.getCounters();
    const stim = this.sources.stimController();
    const c = stim?.getCounters();

    this.human.set('stimulator', status.label, status.simulated ? 'muted' : 'warn');
    this.human.set('connected', status.connected ? 'yes' : 'no', status.connected ? 'ok' : 'muted');
    this.human.set(
      'armed',
      status.armed ? 'ARMED' : 'disarmed',
      status.armed ? (status.simulated ? 'warn' : 'bad') : 'muted',
    );
    this.human.set('requests', String(counters.requested));
    this.human.set('rejected', String(counters.rejected), counters.rejected > 0 ? 'warn' : undefined);
    this.human.set('movements', c ? String(c.movements) : '—', 'ok');
    this.human.set('missed', c ? String(c.missed) : '—', c && c.missed > 0 ? 'warn' : undefined);
    this.human.set(
      'unattributed',
      c ? String(c.unattributed) : '—',
      c && c.unattributed > 0 ? 'warn' : undefined,
    );
    this.human.set(
      'successRate',
      c && c.requested > 0 ? fmtPct(c.movements / c.requested) : '—',
    );
    const deviceStatus = (stim ?? controller).status?.() as Record<string, unknown> | undefined;
    this.human.set('device', String(deviceStatus?.device ?? deviceStatus?.connection ?? '—'));

    // ---- latency ----
    this.renderLatency();
  }

  private renderLatency(): void {
    const stats = this.sources.session.latency.allStats();
    while (this.latencyTable.rows.length > stats.length) {
      this.latencyTable.deleteRow(this.latencyTable.rows.length - 1);
    }
    stats.forEach((s, i) => {
      let row = this.latencyTable.rows[i];
      if (!row) {
        row = this.latencyTable.insertRow();
        for (let c = 0; c < 7; c++) row.insertCell();
      }
      const total = s.stage === ('total' as LatencyStage);
      row.className = total ? 'total-row' : '';
      row.cells[0].textContent = STAGE_LABELS[s.stage];
      row.cells[1].textContent = String(s.count);
      row.cells[2].textContent = fmtMs(s.current);
      row.cells[3].textContent = fmtMs(s.median);
      row.cells[4].textContent = fmtMs(s.mean);
      row.cells[5].textContent = fmtMs(s.p95);
      row.cells[6].textContent = fmtMs(s.jitter);
    });
  }

  resetBest(): void {
    this.best = 0;
  }
}
